import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BaseApi } from '../_base-api';
import { OrderItem, OrderDetailEntity, VendorOrderDetailEntity, OrderCustomerUpdate, OrderItemStatus, OrderItemReportDTO } from '@models';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { DataGetOptions, APIHelper, ItemRecord, SnackbarService } from '@controladad/ng-base';
import { GetApiAdapter } from '../../adapters';

export interface AdminPlaceOrderItem {
  variantId: number;
  countOfOrder: number;
  isGift: boolean;
}

export interface AdminPlaceOrderRequest {
  targetUserId?: number;
  address: {
    governorateId: number;
    areaId: number;
    address: string;
    block?: string;
    street?: string;
    avenue?: string;
    floor?: string;
    apartment?: string;
    buildingNumber?: string;
    houseType?: number;
  };
  paymentMethodId: number;
  items: AdminPlaceOrderItem[];
  customerFullName: string;
  customerCountryCode?: string | null;
  customerFirstName?: string;
  customerLastName?: string;
  customerEmail?: string | null;
  customerPhoneNumber: string;
  couponCode?: string | null;
  note?: string | null;
}

export interface PreviewCheckoutRequest {
  targetUserId?: number;
  governorateId?: number | null;
  couponCode?: string;
  items: AdminPlaceOrderItem[];
}

export interface OrderItemUpdateRequest {
  orderId: number;
  variantId: number;
  quantity: number;
  unitPrice: number;
  variantOrderState: number;
  refundToWallet: boolean;
  forceUpdate: boolean;
}

export interface OrderItemAddRequest {
  orderId: number;
  variantId: number;
  quantity: number;
  variantOrderState: number;
  forceAdd: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class OrderApiService extends BaseApi<OrderItem> {
  private readonly snackbar = inject(SnackbarService);

  constructor() {
    super('/AdminPanel/Order', {
      get: '/AdminPanel/GetOrdersList',
      delete: '/AdminPanel/AdminDeleteOrders',
    });
  }

  // Admin Methods
  // @ts-ignore
  override getAdapter(options?: DataGetOptions<OrderItem>) {
    return GetApiAdapter('/AdminPanel/GetOrdersList', options);
  }

  override get(id: number): Observable<OrderItem> {
    return http$.get<OrderItem>(`/AdminPanel/GetOrderDetails?OrderId=${id}`);
  }

  override delete<T>(ids: number | number[]) {
    const idsArray = typeof ids === 'number' ? [ids] : ids;
    return http$.post<T>('/AdminPanel/AdminDeleteOrders', { orderIds: idsArray });
  }

  bulkChangeStatus(orderIds: number[], status: number) {
    return http$.post('/AdminPanel/BulkChangeOrderStatus', { orderIds, status });
  }

  exportOrders(filters?: any) {
    return this.exportAndDownload('/AdminPanel/ExportOrderList', filters, 'orders.xlsx');
  }

  updateOrderStatus(orderId: number, status: number, refundToWallet: boolean) {
    return http$.post('/AdminPanel/ChangeOrderStatus', { orderId, newStatus: status, refundToWallet });
  }

  getOrderDetails(id: number): Observable<OrderDetailEntity> {
    return http$.get<OrderDetailEntity>(`/AdminPanel/GetAdminOrderDetails?OrderId=${id}`);
  }

  // Cancel a single order
  cancelOrder(orderId: number) {
    return http$.post('/App/CancelOrder', { orderId });
  }

  // Cancel a single order item - Admin uses new API
  cancelOrderItem(orderId: number, variantId: number, refundToWallet: boolean): Observable<object> {
    return http$.post('/AdminPanel/AdminUpdateVariantStatus', {
      orderId,
      variantIds: [variantId],
      status: OrderItemStatus.Cancelled,
      refundToWallet,
    });
  }

  // Send invoice PDF via email
  sendInvoiceEmail(orderId: number) {
    return http$.post('/AdminPanel/SendOrderInvoice', { orderId });
  }

  // Trigger manual ERP sync for one or more orders
  syncOrdersWithErp(orderIds: number[]) {
    return http$.put('/AdminPanel/AdminSyncOrderWithERP', { orderIds });
  }

  syncOrderWithErp(orderId: number) {
    return this.syncOrdersWithErp([orderId]);
  }

  // Download order PDF
  downloadOrderPdf(orderId: number) {
    return http$.get(`/AdminPanel/DownloadOrderPdf?OrderId=${orderId}`, { responseType: 'blob' })
      .pipe(tap((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `order-${orderId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }));
  }

  // Download invoice PDF
  downloadInvoice(orderId: number, invoiceDate?: Date | null, isPreview: boolean = false): Observable<void> {
    let path = `/AdminPanel/DownloadInvoice?OrderId=${orderId}`;
    if (invoiceDate) {
      path += `&dateTime=${encodeURIComponent(invoiceDate.toISOString())}`;
    }

    if (isPreview) {
      return this.openPreviewWithGrant('/AdminPanel/PreviewInvoice', {
        OrderId: `${orderId}`,
        dateTime: invoiceDate ? invoiceDate.toISOString() : undefined,
      });
    }

    return http$.get(path, { responseType: 'blob', observe: 'response' as const })
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          this.handlePdfResponse(response, 'invoice.pdf');
        }),
        map(() => void 0),
      );
  }

  // Update customer information for an order
  updateOrderCustomerInfo(data: OrderCustomerUpdate) {
    return http$.post('/AdminPanel/EditOrder', {
      orderId: data.orderId,
      fields: APIHelper.nestValues(data.fields, false)
    });
  }

  // Admin add order
  adminPlaceOrder(request: AdminPlaceOrderRequest): Observable<any> {
    return http$.post('/AdminPanel/AddOrder', request);
  }

  // Preview checkout (get order summary with payment methods)
  previewCheckout(request: PreviewCheckoutRequest): Observable<any> {
    return http$.post('/AdminPanel/PreviewCheckout', request);
  }

  // Update order item (quantity and override price)
  updateOrderItem(data: OrderItemUpdateRequest): Observable<any> {
    return http$.put('/AdminPanel/UpdateOrderItem', data);
  }

  // Add new item to existing order
  addOrderItem(data: OrderItemAddRequest): Observable<any> {
    return http$.post('/AdminPanel/AddOrderItem', data);
  }

  // Update order payment method
  updateOrderPaymentMethod(orderId: number, paymentMethodId: number): Observable<any> {
    return http$.put('/AdminPanel/ChangeOrderPaymentMethod', {
      orderId,
      paymentMethodId
    });
  }

  bulkDownloadInvoices(orderIds: number[], invoiceDate?: Date | null, isPreview: boolean = false): Observable<void> {
    const body: any = { orderIds };
    if (invoiceDate) {
      body.dateTime = invoiceDate.toISOString();
    }

    if (isPreview) {
      return this.openPreviewWithGrant('/AdminPanel/BulkDownloadInvoicesPreview', {
        OrderIds: orderIds.map((id) => `${id}`),
        dateTime: invoiceDate ? invoiceDate.toISOString() : undefined,
      });
    }

    return http$.post('/AdminPanel/BulkDownloadInvoices', body, { responseType: 'blob', observe: 'response' as const })
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          this.handlePdfResponse(response, 'invoices.pdf');
        }),
        map(() => void 0),
      );
  }

  private handlePdfResponse(response: HttpResponse<Blob>, fallbackFilename: string) {
    const blob = response.body;
    if (!blob) {
      this.snackbar.error('Invalid invoice response. Please try again.');
      return;
    }

    const serverFilename = this.extractFileNameFromDisposition(response.headers.get('Content-Disposition'));
    const filename = this.ensurePdfExtension(serverFilename || fallbackFilename);

    const headerContentType = (response.headers.get('Content-Type') || '').toLowerCase();
    const blobContentType = (blob.type || '').toLowerCase();
    const isHtmlResponse = headerContentType.includes('text/html') || blobContentType.includes('text/html');
    const isPdfLikeResponse =
      headerContentType.includes('application/pdf') ||
      blobContentType.includes('application/pdf') ||
      filename.toLowerCase().endsWith('.pdf');

    if (isHtmlResponse || !isPdfLikeResponse) {
      this.snackbar.error('Could not open invoice preview as PDF. Please try again.');
      return;
    }

    const pdfFile = new File([blob], filename, { type: 'application/pdf' });
    const objectUrl = window.URL.createObjectURL(pdfFile);

    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(objectUrl);
  }

  private extractFileNameFromDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const filenameStar = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i);
    if (filenameStar && filenameStar[1]) {
      const rawValue = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"(.*)"$/, '$1');
      try {
        return decodeURIComponent(rawValue);
      } catch {
        return rawValue;
      }
    }

    const filename = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    if (!filename || !filename[1]) {
      return null;
    }

    return filename[1].trim().replace(/^"(.*)"$/, '$1');
  }

  private ensurePdfExtension(filename: string): string {
    return filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
  }

  private buildPreviewUrl(path: string, params: Record<string, string | string[] | undefined>): string {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const item of value) {
          query.append(key, item);
        }
        continue;
      }

      query.append(key, value);
    }

    const baseUrl = `${this.apiBaseUrl ?? ''}`.replace(/\/+$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const queryString = query.toString();
    return queryString ? `${baseUrl}${normalizedPath}?${queryString}` : `${baseUrl}${normalizedPath}`;
  }

  private issueInvoicePreviewGrant(): Observable<void> {
    return http$.post('/AdminPanel/IssueInvoicePreviewGrant', {}, { withCredentials: true }).pipe(
      map(() => void 0),
    );
  }

  private openPreviewWithGrant(path: string, params: Record<string, string | string[] | undefined>): Observable<void> {
    const previewUrl = this.buildPreviewUrl(path, params);
    const previewWindow = window.open('', '_blank');
    if (!previewWindow) {
      this.snackbar.error('Preview popup was blocked by the browser.');
      return of(void 0);
    }

    return this.issueInvoicePreviewGrant().pipe(
      tap(() => {
        previewWindow.location.href = previewUrl;
      }),
      map(() => void 0),
      catchError(() => {
        previewWindow.close();
        this.snackbar.error('Could not authorize invoice preview. Please try again.');
        return of(void 0);
      }),
    );
  }

  // Vendor Methods
  vendorGetAdapter(options?: DataGetOptions<OrderItem>) {
    return GetApiAdapter('/VendorPanel/GetVendorOrders', options);
  }

  vendorGetOrderDetails(id: number): Observable<VendorOrderDetailEntity> {
    return http$.get<VendorOrderDetailEntity>(`/VendorPanel/GetVendorOrderDetails?OrderId=${id}`);
  }

  vendorMarkOrderAsReady(orderId: number): Observable<object> {
    return http$.post('/VendorPanel/VendorReady', { orderId });
  }

  // Update variant status for multiple items
  vendorUpdateVariantStatus(orderId: number, variantIds: number[], status: number): Observable<object> {
    return http$.post('/VendorPanel/UpdateVariantStatus', {
      orderId,
      variantIds,
      status
    });
  }

  vendorExportOrders(filters?: any) {
    return this.exportAndDownload('/VendorPanel/VendorExportOrderList', filters, 'vendor-orders.xlsx');
  }

  // Order Item Report Methods
  getOrderItemReportAdapter(options?: DataGetOptions<OrderItemReportDTO>) {
    return GetApiAdapter<OrderItemReportDTO>('/AdminPanel/GetOrderItemReport', options);
  }

  exportOrderItemReport(filters?: any) {
    return this.exportAndDownload('/AdminPanel/ExportOrderItemReport', filters, 'order-items-report.xlsx');
  }

  getAllVendors(): Observable<ItemRecord<number>[]> {
    return super.getAll<{ id: number; vendorName: string }>(undefined, '/AdminPanel/GetVendorList')
      .pipe(map((vendors) => vendors.map((v) => ({ value: v.id, label: v.vendorName }))));
  }
}
